// ./src/pages/private/ConnectorsPage/ConnectorsPage.tsx
import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { PageProps } from "@sdflc/react-router-pages";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  SearchInput,
  PremiumFeatureGate,
  FeatureBenefitsList,
} from "@/components/generic";
import {
  ConnectorsTable,
  ConnectorEditModal,
} from "@/components/features/Connectors";
import { useConnectors, useNumericFeature } from "@/hooks";
import { Connector, ConnectorInput, ConnectorFilter } from "@/utils/api";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE, OPERATION_RIGHTS } from "@/config";
import { FEATURE_CODES } from "@/config";
import { STATUSES } from "@sdflc/utils";
import { ENTITIES } from "@/config/userRoles";

const CONNECTORS_BENEFITS = [
  "Connect to 30+ external services including AWS, Azure, GCP, Slack, and more",
  "Route form submissions to webhooks, CRMs, databases, and storage services",
  "Secure credential storage with AES-256 encryption",
  "Use in workflows to automate form data processing",
  "Send emails via your own SMTP, SendGrid, or SES",
  "Store uploaded files in your own S3, Azure Blob, or GCP Storage",
];

export const ConnectorsPage: React.FC<PageProps> = (props: PageProps) => {
  const { hasRightTo, user } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id?: string }>();

  // Check if connectors feature is enabled
  const { isEnabled: isConnectorsEnabled } = useNumericFeature(
    FEATURE_CODES.MAX_CONNECTORS
  );

  // Parse URL to determine which modal to show
  const connectorIdFromUrl = params.id;
  const isAddMode = location.pathname.endsWith("/add");
  const isEditMode =
    !!connectorIdFromUrl && location.pathname.endsWith("/edit");

  const canCreate = hasRightTo(ENTITIES.CONNECTORS, OPERATION_RIGHTS.CREATE);

  // Use the connectors hook for all data management
  const {
    connectors,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    isCreating,
    isUpdating,
    removingId,
    list,
    loadMore,
    refresh,
    get,
    create,
    update,
    remove,
    lastError,
    clearError,
  } = useConnectors({
    autoLoad: isConnectorsEnabled,
    userId: user?.id,
    pageSize: DEFAULT_PAGE_SIZE,
    initialFilters: {
      status: [STATUSES.ACTIVE, STATUSES.PENDING, STATUSES.DISABLED],
    },
  });

  // Local UI state
  const [connectorToDelete, setConnectorToDelete] = useState<Connector | null>(
    null
  );
  const [searchValue, setSearchValue] = useState<string>("");
  const [editingConnector, setEditingConnector] = useState<Connector | null>(
    null
  );
  const [editedConnectorId, setEditedConnectorId] = useState<string | null>(
    null
  );

  // Load connector when in edit mode
  useEffect(() => {
    if (isEditMode && connectorIdFromUrl && isConnectorsEnabled) {
      const loadConnector = async () => {
        await get(connectorIdFromUrl);
      };
      loadConnector();
    }
  }, [isEditMode, connectorIdFromUrl, get, isConnectorsEnabled]);

  // Update editing connector when we get data
  useEffect(() => {
    if (connectorIdFromUrl) {
      const connector = connectors.find((c) => c.id === connectorIdFromUrl);
      if (connector) {
        if (isEditMode) {
          setEditingConnector(connector);
        }
      }
    } else {
      setEditingConnector(null);
    }
  }, [isEditMode, connectorIdFromUrl, connectors]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      const newFilters: ConnectorFilter = {
        ...filters,
        status: [STATUSES.ACTIVE, STATUSES.PENDING, STATUSES.DISABLED],
      };
      if (value.trim()) {
        newFilters.searchKeyword = value.trim();
      } else {
        delete newFilters.searchKeyword;
      }

      list(newFilters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, sortBy, true);
    },
    [filters, sortBy, list]
  );

  // Handle sort change
  const handleSortChange = useCallback(
    (newSortBy: any[]) => {
      list(filters, { page: 1, pageSize: DEFAULT_PAGE_SIZE }, newSortBy, true);
    },
    [filters, list]
  );

  // Handle add button click
  const handleAddConnector = useCallback(() => {
    clearError();
    navigate("/connectors/add");
  }, [navigate, clearError]);

  // Handle edit connector
  const handleEditConnector = useCallback(
    (connector: Connector) => {
      clearError();
      if (connector.id) {
        navigate(`/connectors/${connector.id}/edit`);
      }
    },
    [navigate, clearError]
  );

  // Handle remove connector button click
  const handleRemoveConnectorClick = useCallback((connector: Connector) => {
    setConnectorToDelete(connector);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (connectorToDelete?.id) {
      const success = await remove(connectorToDelete.id);
      if (success) {
        setConnectorToDelete(null);
        if (connectorIdFromUrl === connectorToDelete.id) {
          navigate("/connectors");
        }
      }
    }
  }, [connectorToDelete, remove, connectorIdFromUrl, navigate]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setConnectorToDelete(null);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setEditingConnector(null);
    clearError();
    navigate("/connectors");
  }, [navigate, clearError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refresh();
  }, [refresh]);

  // Handle connector creation
  const handleCreateConnector = useCallback(
    async (data: ConnectorInput): Promise<Connector | null> => {
      const result = await create(data);
      if (result?.id) {
        setEditedConnectorId(result.id);
        setTimeout(() => setEditedConnectorId(null), 3000);
      }
      return result;
    },
    [create]
  );

  // Handle connector update
  const handleUpdateConnector = useCallback(
    async (id: string, data: ConnectorInput): Promise<Connector | null> => {
      const result = await update(id, data);
      if (result?.id) {
        setEditedConnectorId(result.id);
        setTimeout(() => setEditedConnectorId(null), 3000);
      }
      return result;
    },
    [update]
  );

  return (
    <DashboardLayout {...props}>
      <PremiumFeatureGate
        featureCode={FEATURE_CODES.MAX_CONNECTORS}
        featureTitle="Connectors"
        forceEnabled={isConnectorsEnabled}
        featureDescription={
          <FeatureBenefitsList benefits={CONNECTORS_BENEFITS} />
        }
      >
        <Stack gap="md">
          <AppToolbar
            preset="page-header"
            sections={[
              {
                id: "actions",
                items: [
                  <AppButton
                    key="add-connector"
                    iconName="Plus"
                    onClick={handleAddConnector}
                    disabled={
                      isCreating || !!removingId || loading || !canCreate
                    }
                  >
                    Add
                  </AppButton>,
                  <AppButton
                    key="refresh"
                    variant="secondary"
                    iconName="Refresh"
                    onClick={handleRefresh}
                    disabled={isCreating || !!removingId || loading}
                  />,
                ],
                align: "left",
                gap: "sm",
              },
              {
                id: "search",
                items: [
                  <SearchInput
                    key="search"
                    placeholder="Search connectors..."
                    value={searchValue}
                    onChange={(value) => handleSearchChange(value)}
                    style={{ minWidth: 250 }}
                  />,
                ],
                align: "right",
              },
            ]}
          />

          <ConnectorsTable
            connectors={connectors}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            onLoadMore={loadMore}
            onEditConnector={handleEditConnector}
            onRemoveConnector={handleRemoveConnectorClick}
            removingConnectorId={removingId}
            editedItemId={editedConnectorId}
          />

          <EmptyState
            display={isEmpty && !loading}
            title="No connectors yet"
            description="Add connectors for webhooks, storage, messaging, and CRM integrations"
            actionIcon="Plus"
            actionLabel="Add Connector"
            onAction={handleAddConnector}
          />
        </Stack>

        <ConnectorEditModal
          opened={isAddMode || isEditMode}
          onClose={handleEditModalClose}
          connector={isEditMode ? editingConnector : null}
          onCreateConnector={handleCreateConnector}
          onUpdateConnector={handleUpdateConnector}
          isCreating={isCreating}
          isUpdating={isUpdating}
          lastError={lastError}
          onClearError={clearError}
        />

        <ConfirmModal
          opened={!!connectorToDelete}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Delete Connector"
          message={`Are you sure you want to delete "${connectorToDelete?.name}"?`}
          details="This action cannot be undone. The connector will be removed from all workflows using it."
          confirmInProgress={removingId === connectorToDelete?.id}
          confirmLabel="Delete"
          type="danger"
        />
      </PremiumFeatureGate>
    </DashboardLayout>
  );
};
